import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/password-input';
import { AuthLayout } from '@/layouts/AuthLayout';
import { api, errorMessage, isApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const schema = z
  .object({
    password: z.string().min(10, 'Use at least 10 characters').max(128),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, { message: 'Passwords do not match', path: ['confirm'] });
type Values = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { adoptSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { password: '', confirm: '' } });

  const onSubmit = form.handleSubmit(async ({ password }) => {
    setError(null);
    try {
      const session = await api.auth.resetPassword(token, password);
      await adoptSession(session);
      navigate('/jobs', { replace: true });
    } catch (err) {
      if (isApiError(err) && err.status === 422) {
        const fe = err.fieldErrors();
        if (fe['password']) form.setError('password', { message: fe['password'] });
      }
      setError(errorMessage(err));
    }
  });

  return (
    <AuthLayout demo={false}
      title="Choose a new password"
      description="Pick something you do not use anywhere else. You will be signed in right after."
      footer={
        <Link to="/login" className="hover:text-foreground underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      {!token ? (
        <Alert variant="destructive">
          <AlertTitle>Missing reset link</AlertTitle>
          <AlertDescription>This page needs the link from your reset email. Request a new one from the sign-in page.</AlertDescription>
        </Alert>
      ) : (
        <form onSubmit={onSubmit} className="grid gap-5" noValidate>
          <FormError message={error} />
          <Field label="New password" htmlFor="password" description="At least 10 characters." error={form.formState.errors.password?.message}>
            <PasswordInput id="password" autoComplete="new-password" autoFocus aria-invalid={!!form.formState.errors.password} {...form.register('password')} />
          </Field>
          <Field label="Confirm password" htmlFor="confirm" error={form.formState.errors.confirm?.message}>
            <PasswordInput id="confirm" autoComplete="new-password" capsLockHint={false} aria-invalid={!!form.formState.errors.confirm} {...form.register('confirm')} />
          </Field>
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Saving…' : 'Set password and sign in'}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
